/*
 * Custom migrated scripts from Backup Alori.
 * (Shapes 5 migration: curved-text wave is now injected here, see injectCurvedTextWaves.)
 * Phase 1 only: isolated store-specific behavior and dormant hooks.
 * Updated Alori / Shapes 4.3.1 remains the source of truth.
 * Sticky ATC removed — replaced by snippets/sticky-atc.liquid
 */

(() => {
  const BLOG_SCROLL_RATIO = 0.85;

  function initBlogCarousel(track) {
    if (!track || track.dataset.customMigratedBlogCarousel === 'true') {
      return;
    }

    const scope = track.closest('.shopify-section, section, [id]') || document;
    const sectionId =
      track.id && track.id.indexOf('blog-carousel-') === 0
        ? track.id.replace('blog-carousel-', '')
        : track.dataset.sectionId;
    const dotsContainer = sectionId
      ? document.getElementById(`dots-${sectionId}`)
      : scope.querySelector('.blog-carousel-dots');
    const items = Array.from(track.querySelectorAll('.blog-carousel-item'));

    if (!dotsContainer || !items.length) {
      return;
    }

    const dots = Array.from(dotsContainer.querySelectorAll('.blog-carousel-dot'));
    const prevButton = scope.querySelector('.blog-carousel-prev');
    const nextButton = scope.querySelector('.blog-carousel-next');

    const updateActiveDot = () => {
      const itemWidth = items[0] ? items[0].offsetWidth + 16 : 1;
      const activeIndex = Math.round(track.scrollLeft / itemWidth);

      dots.forEach((dot, index) => {
        dot.classList.toggle('active', index === activeIndex);
      });
    };

    dotsContainer.addEventListener('click', (event) => {
      const dot = event.target.closest('.blog-carousel-dot');
      if (!dot) {
        return;
      }

      const index = Number.parseInt(dot.dataset.index || '', 10);
      if (Number.isNaN(index) || !items[index]) {
        return;
      }

      track.scrollTo({
        left: items[index].offsetLeft - track.offsetLeft,
        behavior: 'smooth',
      });
    });

    if (prevButton) {
      prevButton.addEventListener('click', () => {
        track.scrollBy({
          left: -track.offsetWidth * BLOG_SCROLL_RATIO,
          behavior: 'smooth',
        });
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', () => {
        track.scrollBy({
          left: track.offsetWidth * BLOG_SCROLL_RATIO,
          behavior: 'smooth',
        });
      });
    }

    track.addEventListener('scroll', updateActiveDot, { passive: true });
    track.dataset.customMigratedBlogCarousel = 'true';
    updateActiveDot();
  }

  /*
   * ALORI: turn the stock Shapes "Blog posts" section into the swipe carousel
   * (track + dots + arrows) at runtime, so no stock or forked section file is
   * needed and Shapes updates to blog-posts.liquid still apply.
   */
  function enhanceBlogPostsSections(root) {
    root
      .querySelectorAll('.shopify-section[id*="__blog_posts"]')
      .forEach((wrapper) => {
        const sectionId = wrapper.id.replace('shopify-section-', '');
        if (document.getElementById(`blog-carousel-${sectionId}`)) {
          return;
        }
        const list = wrapper.querySelector('section ul');
        if (!list) {
          return;
        }
        const items = Array.from(list.children).filter((el) => el.tagName === 'LI');
        if (!items.length) {
          return;
        }
        list.id = `blog-carousel-${sectionId}`;
        ['grid', 'grid-cols-1', 'gap-10', 'lg:grid-cols-12'].forEach((c) => list.classList.remove(c));
        list.classList.add('blog-carousel-track');
        items.forEach((li) => {
          Array.from(li.classList)
            .filter((c) => /(^|:)col-span-/.test(c))
            .forEach((c) => li.classList.remove(c));
          li.classList.add('blog-carousel-item');
        });
        const nav = document.createElement('div');
        nav.className = 'blog-carousel-nav';
        nav.setAttribute('aria-hidden', 'true');
        nav.innerHTML =
          '<button type="button" class="blog-carousel-btn blog-carousel-prev">&#8249;</button>' +
          `<div class="blog-carousel-dots" id="dots-${sectionId}">` +
          items
            .map((_, i) => `<span class="blog-carousel-dot${i === 0 ? ' active' : ''}" data-index="${i}"></span>`)
            .join('') +
          '</div>' +
          '<button type="button" class="blog-carousel-btn blog-carousel-next">&#8250;</button>';
        list.insertAdjacentElement('afterend', nav);
      });
  }

  function initBlogCarousels(root) {
    enhanceBlogPostsSections(root);
    root
      .querySelectorAll('[id^="blog-carousel-"], .blog-carousel-track')
      .forEach(initBlogCarousel);
  }

  function initProductThumbnailRail(rail) {
    if (!rail || rail.dataset.customMigratedThumbRail === 'true') {
      return;
    }

    const syncActiveThumbnail = () => {
      const activeThumbnail = rail.querySelector(
        '.product-thumbnail-list-item--active'
      );

      if (!activeThumbnail) {
        return;
      }

      activeThumbnail.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    };

    if ('MutationObserver' in window) {
      const observer = new MutationObserver((mutations) => {
        const classChanged = mutations.some(
          (mutation) =>
            mutation.type === 'attributes' &&
            mutation.attributeName === 'class' &&
            mutation.target instanceof Element &&
            mutation.target.classList.contains('product-thumbnail-list-item')
        );

        if (classChanged) {
          syncActiveThumbnail();
        }
      });

      observer.observe(rail, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    rail.querySelectorAll('.product-thumbnail-list-item').forEach((item) => {
      item.addEventListener('click', () => {
        window.requestAnimationFrame(syncActiveThumbnail);
      });
    });

    rail.dataset.customMigratedThumbRail = 'true';
    syncActiveThumbnail();
  }

  function initProductThumbnailRails(root) {
    root
      .querySelectorAll('.alori-thumb-strip, .alori-mobile-thumb-strip')
      .forEach(initProductThumbnailRail);
  }

  function initCurvedTextWave(waveWrapper) {
    if (!waveWrapper || waveWrapper.dataset.customMigratedWave === 'true') {
      return;
    }

    const suffix = waveWrapper.id.replace('alori-wave-', '');
    if (!suffix) {
      return;
    }

    const section = document.getElementById(suffix) || waveWrapper.parentElement;
    const waveSvg = document.getElementById(`alori-wave-svg-${suffix}`);

    if (!section || !waveSvg) {
      return;
    }

    section.style.position = section.style.position || 'relative';
    waveSvg.style.transform = 'translateX(-800px)';
    waveWrapper.dataset.customMigratedWave = 'true';
  }

  /*
   * ALORI: inject the decorative pink wave into every Shapes curved-text section.
   * (Previously hard-coded inside sections/curved-text.liquid, which Shapes
   * updates overwrite. Injecting it here means no stock file needs editing.)
   */
  const WAVE_PATH =
    'M0,140 Q200,240 400,140 T800,140 T1200,140 T1600,140 T2000,140 T2400,140 T2800,140 T3200,140 T3600,140 T4000,140 T4400,140 T4800,140 T5200,140 T5600,140 T6000,140 T6400,140 L6400,200 L0,200 Z';

  function injectCurvedTextWaves(root) {
    root
      .querySelectorAll('data-island[x-data^="CurvedText"][id]')
      .forEach((island) => {
        const id = island.id;
        if (document.getElementById(`alori-wave-${id}`)) {
          return;
        }
        const wrapper = document.createElement('div');
        wrapper.id = `alori-wave-${id}`;
        wrapper.setAttribute('aria-hidden', 'true');
        wrapper.innerHTML =
          `<svg id="alori-wave-svg-${id}" width="6400" height="200" xmlns="http://www.w3.org/2000/svg">` +
          `<path id="alori-wave-path-${id}" fill="rgb(246,197,193)" d="${WAVE_PATH}"/></svg>`;
        island.appendChild(wrapper);
      });
  }

  function initCurvedTextWaves(root) {
    injectCurvedTextWaves(root);
    root.querySelectorAll('[id^="alori-wave-"]').forEach((waveWrapper) => {
      initCurvedTextWave(waveWrapper);
    });
  }

  function run(root) {
    initBlogCarousels(root);
    initProductThumbnailRails(root);
    initCurvedTextWaves(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => run(document));
  } else {
    run(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) {
      run(event.target);
    }
  });
})();
